/**
 * The interviewer setup's requests, and a strict reading of their answers.
 *
 * Everything avrio-backend returns is read field by field: a section key the
 * page doesn't know is dropped, numbers are checked, and a reply that isn't a
 * config at all is an error the page can say, never a half-drawn card. Errors
 * come back as one InterviewerError whose `kind` the screens switch on, so no
 * screen reads axios internals.
 *
 * Rio's contract (avrio-rio docs/interviewer-setup.md): a config always lists
 * the five sections once each, minutes optional; the version lives in `meta`
 * and beside the config in each reply; a 422 carries `problems` and a 409 the
 * `currentVersion`. A config goes back to Rio with Rio's own fields only
 * (toRioConfig): never a field the preview adds, which Rio refuses.
 */
import { isAxiosError } from 'axios';
import axios from '../../../axiosConfig';
import {
  SCHEMA_VERSION,
  SECTION_KEYS,
  TONES,
  WORKSPACE_KINDS,
  type CustomQuestion,
  type DiffEntry,
  type DraftedBar,
  type DraftResult,
  type EditResult,
  type FieldError,
  type HistoryVersion,
  type InterviewerConfig,
  type InterviewerPreview,
  type InterviewerSection,
  type QuestionExpectations,
  type InterviewerService,
  type Posting,
  type PreviewSection,
  type SaveResult,
  type SavedInterviewer,
  type SectionKey,
  type SkillRef,
  type Tone,
  type ValidationResult,
  type WorkspaceKind,
  type WorkspaceTask,
} from './interviewer.types';
import {
  kindsOf,
  pinSections,
  rebalance,
  summarizeChange,
} from '../interviewer/model';

export type InterviewerErrorKind =
  | 'validation'
  | 'conflict'
  | 'not_found'
  | 'forbidden'
  | 'network'
  | 'server'
  | 'malformed';

/** A 403: trying again won't help, so the screens offer no Try again. */
export const FORBIDDEN_MESSAGE =
  "This account can view jobs but can't change Rio's interviewer.";

export class InterviewerError extends Error {
  readonly kind: InterviewerErrorKind;
  readonly errors: FieldError[];
  /** On a conflict: the version someone else saved. */
  readonly currentVersion: number | null;

  constructor(
    kind: InterviewerErrorKind,
    message: string,
    errors: FieldError[] = [],
    currentVersion: number | null = null,
  ) {
    super(message);
    this.name = 'InterviewerError';
    this.kind = kind;
    this.errors = errors;
    this.currentVersion = currentVersion;
  }
}

type Obj = Record<string, unknown>;

const isObj = (value: unknown): value is Obj =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const strOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const int = (value: unknown, min: number, max: number): number | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, Math.round(value)))
    : null;

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v !== '')
    : [];

const isSectionKey = (value: unknown): value is SectionKey =>
  typeof value === 'string' &&
  (SECTION_KEYS as readonly string[]).includes(value);

const isTone = (value: unknown): value is Tone =>
  typeof value === 'string' && (TONES as readonly string[]).includes(value);

const isWorkspaceKind = (value: unknown): value is WorkspaceKind =>
  typeof value === 'string' &&
  (WORKSPACE_KINDS as readonly string[]).includes(value);

/**
 * A set of work-task kinds as Rio reads it: a list of known kinds, none twice,
 * with `auto` only on its own. Anything else is not a set, so null.
 */
const kindSet = (value: unknown): WorkspaceKind[] | null => {
  if (!Array.isArray(value) || !value.length) return null;
  if (!value.every(isWorkspaceKind)) return null;
  if (new Set(value).size !== value.length) return null;
  return value.includes('auto') && value.length > 1 ? null : value;
};

/**
 * The work-task kinds as Rio reads them (interviewer_plan.workspace_kinds), so
 * the card never shows a set that was not running: a good set that starts with
 * `kind`, otherwise just `kind`. Absent, malformed (an unknown kind, a repeat,
 * `auto` beside others, empty) or starting with another kind (an older card
 * changed `kind` alone) all mean `kind` alone.
 */
export const parseKinds = (
  value: unknown,
  kind: WorkspaceKind,
): WorkspaceKind[] => {
  const kinds = kindSet(value);
  return kinds && kinds[0] === kind ? kinds : [kind];
};

const malformed = () =>
  new InterviewerError(
    'malformed',
    "Rio's reply couldn't be read. Try again in a moment.",
  );

const VERSION_MAX = Number.MAX_SAFE_INTEGER;

/**
 * The five sections, once each. Minutes are optional on the wire: they come
 * from Rio's preview when it has them, and otherwise the enabled sections
 * share the target length. A section Rio left out is added, switched off, so
 * the card can still offer it.
 */
const parseSections = (
  value: unknown,
  target: number,
  preview: InterviewerPreview | null,
): InterviewerSection[] => {
  if (!Array.isArray(value)) throw malformed();
  const seen = new Set<SectionKey>();
  const parsed: {
    key: SectionKey;
    enabled: boolean;
    minutes: number | null;
  }[] = [];
  for (const item of value) {
    if (!isObj(item) || !isSectionKey(item.key) || seen.has(item.key)) continue;
    seen.add(item.key);
    parsed.push({
      key: item.key,
      enabled: item.enabled === true,
      minutes: int(item.minutes, 0, 60),
    });
  }
  if (!parsed.length) throw malformed();
  SECTION_KEYS.forEach((key) => {
    if (!seen.has(key)) parsed.push({ key, enabled: false, minutes: null });
  });
  const fromPreview = new Map(
    preview?.sections.map((s) => [s.key, s.minutes]) ?? [],
  );
  const missing = parsed.some((s) => s.minutes === null || s.minutes === 0);
  const filled: InterviewerSection[] = parsed.map((s) => ({
    key: s.key,
    enabled: s.enabled,
    minutes: s.minutes || fromPreview.get(s.key) || 5,
  }));
  // Without minutes from Rio for every enabled section, share the target.
  const total = filled
    .filter((s) => s.enabled)
    .reduce((n, s) => n + s.minutes, 0);
  const pinned = pinSections(filled);
  return missing && total !== target ? rebalance(pinned, target) : pinned;
};

/** Rio's bar for a question: up to five signals per level, or none yet. */
const parseExpectations = (value: unknown): QuestionExpectations | null => {
  if (!isObj(value)) return null;
  const level = (v: unknown) =>
    strings(v)
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 5);
  return {
    excellent: level(value.excellent),
    adequate: level(value.adequate),
    weak: level(value.weak),
  };
};

/** The schema-2 fields a question carries in a config and in a preview. */
const parseQuestionBar = (item: Obj) => ({
  area: strOrNull(item.area),
  areaLabel: strOrNull(item.areaLabel),
  expectations: parseExpectations(item.expectations),
  // As Rio reads it: a question counts only when it says so.
  counts: item.counts === true,
});

const parseCustomQuestions = (value: unknown): CustomQuestion[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        if (!isObj(item) || typeof item.id !== 'string') return [];
        const text = str(item.text).trim();
        if (!text || !isSectionKey(item.section)) return [];
        return [
          {
            id: item.id,
            text,
            section: item.section,
            followUp: item.followUp === true,
            ...parseQuestionBar(item),
          },
        ];
      })
    : [];

export const parseConfig = (
  value: unknown,
  preview: InterviewerPreview | null = null,
): InterviewerConfig => {
  if (!isObj(value)) throw malformed();
  const workspace = isObj(value.workspace) ? value.workspace : {};
  const kind = isWorkspaceKind(workspace.kind) ? workspace.kind : 'auto';
  const meta = isObj(value.meta) ? value.meta : {};
  const target =
    int(value.targetMinutes, 15, 45) ??
    Math.min(45, Math.max(15, preview?.totalMinutes ?? 30));
  return {
    schema: int(value.schema, 1, 1000) ?? 1,
    sections: parseSections(value.sections, target, preview),
    emphasize: strings(value.emphasize).slice(0, 5),
    deemphasize: strings(value.deemphasize).slice(0, 5),
    customQuestions: parseCustomQuestions(value.customQuestions),
    avoidTopics: strings(value.avoidTopics),
    tone: isTone(value.tone) ? value.tone : 'neutral',
    workspace: {
      enabled: workspace.enabled === true,
      kind,
      kinds: parseKinds(workspace.kinds, kind),
    },
    targetMinutes: target,
    meta: {
      createdFrom: str(meta.createdFrom, 'posting'),
      updatedBy: strOrNull(meta.updatedBy),
      updatedAt: strOrNull(meta.updatedAt),
      version: int(meta.version, 0, VERSION_MAX),
    },
  };
};

/**
 * The config as Rio reads it, for edit, validate and save: Rio's own fields
 * only. A question's areaLabel is the page's (Rio refuses it inside a config),
 * and an empty area or bar is left out rather than sent as null. A question
 * just added goes without counts, so the bar Rio writes decides. Who saved
 * and when are Rio's to stamp, so they're never sent.
 */
export const toRioConfig = (config: InterviewerConfig) => ({
  schema: Math.max(config.schema, SCHEMA_VERSION),
  sections: config.sections.map(({ key, enabled, minutes }) => ({
    key,
    enabled,
    minutes,
  })),
  emphasize: config.emphasize,
  deemphasize: config.deemphasize,
  customQuestions: config.customQuestions.map((q) => ({
    id: q.id,
    text: q.text,
    section: q.section,
    followUp: q.followUp,
    ...(q.area ? { area: q.area } : {}),
    ...(q.expectations
      ? {
          expectations: {
            excellent: q.expectations.excellent,
            adequate: q.expectations.adequate,
            weak: q.expectations.weak,
          },
        }
      : {}),
    ...(q.countsFromBar ? {} : { counts: q.counts }),
  })),
  avoidTopics: config.avoidTopics,
  tone: config.tone,
  // `kind` mirrors the first of `kinds`: it is all an older Rio reads.
  workspace: {
    enabled: config.workspace.enabled,
    kind: kindsOf(config)[0],
    kinds: kindsOf(config),
  },
  targetMinutes: config.targetMinutes,
  meta: {
    createdFrom: config.meta.createdFrom,
    ...(config.meta.version !== null ? { version: config.meta.version } : {}),
  },
});

const parseSkillRefs = (value: unknown): SkillRef[] =>
  Array.isArray(value)
    ? value.flatMap((item) =>
        isObj(item) && typeof item.key === 'string'
          ? [{ key: item.key, label: str(item.label, item.key) }]
          : [],
      )
    : [];

/**
 * The work-task kinds Rio says open as asked, in the page's order: a kind the
 * page doesn't know is dropped. null when Rio didn't send a usable list, so the
 * card keeps marking the canvases as coming soon: no list (an older Rio), or a
 * list without Rio's pick and code, which always open.
 */
const parseAvailableKinds = (value: unknown): WorkspaceKind[] | null => {
  if (!Array.isArray(value)) return null;
  const kinds = WORKSPACE_KINDS.filter((kind) => value.includes(kind));
  return kinds.includes('auto') && kinds.includes('code') ? kinds : null;
};

/**
 * Rio's notes on the work task for each kind: known kinds only, string lists
 * only. null unless every kind has a list, so a partial answer is never read
 * as "no notes" for the kinds it leaves out.
 */
const parseNotesByKind = (
  value: unknown,
): Record<WorkspaceKind, string[]> | null => {
  if (!isObj(value)) return null;
  const out = {} as Record<WorkspaceKind, string[]>;
  for (const kind of WORKSPACE_KINDS) {
    const notes = value[kind];
    if (!Array.isArray(notes) || !notes.every((n) => typeof n === 'string')) {
      return null;
    }
    out[kind] = notes;
  }
  return out;
};

/** The tasks Rio would open (preview, with several tasks on): well-formed ones only. */
const parseTasks = (value: unknown): WorkspaceTask[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        const minutes = isObj(item) ? int(item.minutes, 1, 999) : null;
        if (!isObj(item) || !isWorkspaceKind(item.kind) || minutes === null) {
          return [];
        }
        return [
          {
            kind: item.kind,
            label: str(item.label, 'Work task'),
            minutes,
            note: strOrNull(item.note),
          },
        ];
      })
    : [];

export const parsePreview = (value: unknown): InterviewerPreview => {
  if (!isObj(value) || !Array.isArray(value.sections)) throw malformed();
  const sections: PreviewSection[] = value.sections.flatMap((item) => {
    if (!isObj(item) || !isSectionKey(item.key)) return [];
    return [
      {
        key: item.key,
        title: str(item.title, item.key),
        minutes: int(item.minutes, 0, 60) ?? 0,
        enabled: item.enabled === true,
        summary: str(item.summary),
        exampleQuestions: strings(item.exampleQuestions).slice(0, 3),
      },
    ];
  });
  const workspace = isObj(value.workspace) ? value.workspace : {};
  const emphasis = isObj(value.emphasis) ? value.emphasis : {};
  return {
    totalMinutes:
      int(value.totalMinutes, 0, 120) ??
      sections.filter((s) => s.enabled).reduce((sum, s) => sum + s.minutes, 0),
    sections,
    workspace: {
      enabled: workspace.enabled === true,
      kind: isWorkspaceKind(workspace.kind) ? workspace.kind : 'auto',
      label: str(workspace.label, 'Work task'),
      note: strOrNull(workspace.note),
      availableKinds: parseAvailableKinds(workspace.availableKinds),
      notesByKind: parseNotesByKind(workspace.notesByKind),
      // Only a Rio that says `multi: true` opens several; an older Rio sends
      // none of these, and the card stays single.
      ...parseMulti(workspace),
    },
    emphasis: {
      emphasize: parseSkillRefs(emphasis.emphasize),
      deemphasize: parseSkillRefs(emphasis.deemphasize),
    },
    customQuestions: Array.isArray(value.customQuestions)
      ? value.customQuestions.flatMap((item) =>
          isObj(item) &&
          typeof item.id === 'string' &&
          typeof item.text === 'string' &&
          isSectionKey(item.section)
            ? [
                {
                  id: item.id,
                  text: item.text,
                  section: item.section,
                  ...parseQuestionBar(item),
                },
              ]
            : [],
        )
      : [],
    notes: strings(value.notes),
    areas: parseSkillRefs(value.areas),
  };
};

const parseMulti = (workspace: Obj) => {
  if (workspace.multi !== true) {
    return { multi: false, kinds: null, tasks: null, tasksMinutes: null };
  }
  const tasks = parseTasks(workspace.tasks);
  return {
    multi: true,
    kinds: kindSet(workspace.kinds),
    tasks,
    tasksMinutes:
      int(workspace.tasksMinutes, 0, 999) ??
      tasks.reduce((sum, t) => sum + t.minutes, 0),
  };
};

const parseDraft = (value: unknown): DraftResult => {
  if (!isObj(value)) throw malformed();
  const preview = parsePreview(value.preview);
  return { config: parseConfig(value.config, preview), preview };
};

const parseChanges = (value: unknown) =>
  Array.isArray(value)
    ? value.flatMap((item) =>
        isObj(item) && typeof item.summary === 'string'
          ? [
              {
                op: str(item.op),
                summary: item.summary,
                reason: str(item.reason),
              },
            ]
          : [],
      )
    : [];

const parseEdit = (value: unknown): EditResult => {
  const { config, preview } = parseDraft(value);
  const body = value as Obj;
  const diff: DiffEntry[] = Array.isArray(body.diff)
    ? body.diff.flatMap((item) =>
        isObj(item) && typeof item.path === 'string'
          ? [{ path: item.path, before: item.before, after: item.after }]
          : [],
      )
    : [];
  return {
    config,
    preview,
    applied: parseChanges(body.applied).map(({ op, summary }) => ({
      op,
      summary,
    })),
    rejected: parseChanges(body.rejected).map(({ op, summary, reason }) => ({
      op,
      summary,
      reason: reason || "Rio can't make that change.",
    })),
    diff,
  };
};

/** Rio's `problems: [{path, reason}]`, or the older `errors: [{path, message}]`. */
const parseProblems = (value: unknown): FieldError[] => {
  if (!isObj(value)) return [];
  const list = Array.isArray(value.problems)
    ? value.problems
    : Array.isArray(value.errors)
      ? value.errors
      : [];
  return list.flatMap((item) => {
    if (!isObj(item)) return [];
    const message = str(item.reason) || str(item.message);
    return message ? [{ path: str(item.path), message }] : [];
  });
};

/** Every failure as one error the screens can switch on, in plain words. */
export const toInterviewerError = (error: unknown): InterviewerError => {
  if (error instanceof InterviewerError) return error;
  if (!isAxiosError(error)) {
    return new InterviewerError(
      'server',
      'Something on our side failed. Try again.',
    );
  }
  const status = error.response?.status;
  const data = error.response?.data;
  if (status === undefined) {
    return new InterviewerError(
      'network',
      "Couldn't reach Avrio. Check your connection and try again.",
    );
  }
  if (status === 422) {
    const errors = parseProblems(data);
    return new InterviewerError(
      'validation',
      errors[0]?.message ?? "Rio can't use that. Change it and try again.",
      errors,
    );
  }
  if (status === 409) {
    return new InterviewerError(
      'conflict',
      'Someone else saved changes to this interviewer while you were editing.',
      [],
      isObj(data) ? int(data.currentVersion, 0, VERSION_MAX) : null,
    );
  }
  if (status === 404) {
    return new InterviewerError(
      'not_found',
      "This job's interviewer wasn't found.",
    );
  }
  if (status === 403) {
    return new InterviewerError('forbidden', FORBIDDEN_MESSAGE);
  }
  return new InterviewerError(
    'server',
    'Something on our side failed. Try again.',
  );
};

const request = async <T>(
  run: () => Promise<{ data: unknown }>,
  parse: (d: unknown) => T,
) => {
  let data: unknown;
  try {
    data = (await run()).data;
  } catch (error) {
    throw toInterviewerError(error);
  }
  return parse(data);
};

const versionOf = (data: Obj, config: InterviewerConfig | null) =>
  int(data.version, 0, VERSION_MAX) ?? config?.meta.version ?? null;

const parseSaved = (data: unknown): SaveResult => {
  if (!isObj(data)) throw malformed();
  const preview = parsePreview(data.preview);
  const config = parseConfig(data.config, preview);
  return { config, version: versionOf(data, config), preview };
};

/** Rio's bar for one question: an area only from Rio's own list. */
const parseDraftedBar = (data: unknown): DraftedBar => {
  if (!isObj(data)) throw malformed();
  const expectations = parseExpectations(data.expectations);
  return {
    area: strOrNull(data.area),
    areaLabel: strOrNull(data.areaLabel),
    expectations,
    counts: data.counts === true && expectations !== null,
    note: strOrNull(data.note),
  };
};

/**
 * Newest first, each with a line saying what it changed. The version in use
 * comes first; a reset to Rio's default has no config. Avrio sends who saved
 * each one as a name, or nothing: never a staff id.
 */
const parseHistory = (data: unknown): HistoryVersion[] => {
  if (!isObj(data) || !Array.isArray(data.history)) return [];
  const entries = data.history
    .flatMap((item) => {
      if (!isObj(item) || typeof item.version !== 'number') return [];
      let config: InterviewerConfig | null = null;
      try {
        config = parseConfig(item.config);
      } catch {
        config = null;
      }
      return [
        {
          version: item.version,
          updatedAt: str(item.updatedAt),
          updatedBy: str(item.updatedBy).trim() || 'A teammate',
          config,
        },
      ];
    })
    .sort((a, b) => a.version - b.version);
  return entries
    .map((entry, i) => ({
      version: entry.version,
      updatedAt: entry.updatedAt,
      updatedBy: entry.updatedBy,
      summary: entry.config
        ? summarizeChange(i > 0 ? entries[i - 1].config : null, entry.config)
        : "Back to Rio's default",
    }))
    .reverse();
};

export const httpInterviewerService: InterviewerService = {
  draft: (posting: Posting) =>
    request(
      () => axios.post('/api/interviewer/draft', { posting }),
      parseDraft,
    ),

  edit: (config, instruction, posting) =>
    request(
      () =>
        axios.post('/api/interviewer/edit', {
          config: toRioConfig(config),
          instruction,
          posting,
        }),
      parseEdit,
    ),

  draftBar: (question, posting) =>
    request(
      () =>
        axios.post('/api/interviewer/draft-bar', {
          question: { text: question.text, section: question.section },
          posting,
        }),
      parseDraftedBar,
    ),

  get: (jobId) =>
    request(
      () => axios.get(`/api/jobs/${jobId}/interviewer`),
      (data): SavedInterviewer => {
        if (!isObj(data)) throw malformed();
        const preview = parsePreview(data.preview);
        const config =
          data.config === null || data.config === undefined
            ? null
            : parseConfig(data.config, preview);
        return { config, version: versionOf(data, config), preview };
      },
    ),

  save: (jobId, config, expectedVersion) =>
    request(
      () =>
        axios.put(`/api/jobs/${jobId}/interviewer`, {
          config: toRioConfig(config),
          expectedVersion,
        }),
      parseSaved,
    ),

  reset: (jobId, expectedVersion) =>
    request(
      () =>
        expectedVersion === null
          ? axios.delete(`/api/jobs/${jobId}/interviewer`)
          : axios.delete(`/api/jobs/${jobId}/interviewer`, {
              params: { expectedVersion },
            }),
      (data) => ({
        version: isObj(data) ? int(data.version, 0, VERSION_MAX) : null,
      }),
    ),

  history: (jobId) =>
    request(
      () => axios.get(`/api/jobs/${jobId}/interviewer/history`),
      parseHistory,
    ),

  validate: (config, posting) =>
    request(
      () =>
        axios.post('/api/interviewer/validate', {
          config: toRioConfig(config),
          posting,
        }),
      (data): ValidationResult => {
        if (!isObj(data)) throw malformed();
        let preview: InterviewerPreview | null = null;
        try {
          preview = data.preview ? parsePreview(data.preview) : null;
        } catch {
          preview = null;
        }
        return {
          valid: data.valid === true,
          problems: parseProblems(data),
          preview,
        };
      },
    ),
};
